// Keep names in sync with benchling/machine_learning/parameter_optimization/parameter_optimization_constants.py
import {ColumnValidationMap} from 'data-frames/util/AccessorUtils';
import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {LeafType, NUMERICAL_LEAF_TYPES} from 'data-types/benchling-type-def/BenchlingTypeDef';

import {
  AllDataColumns,
  AllDataColumnsMapping,
  FeatureImportanceColumns,
  FeatureImportanceColumnsMapping,
  ShapValuesColumns,
  ShapValuesColumnsMapping,
} from './ParameterOptimizationTypes';

const BASE_FEATURES_VALIDATION_OPTIONS = {
  allowedLeafTypes: [LeafType.TEXT],
  nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
  listType: ColumnTypeValidationFlag.REQUIRED,
  maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
};
const BASE_ENTITIES_VALIDATION_OPTIONS = {
  allowedLeafTypes: [LeafType.TEXT],
  nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
  listType: ColumnTypeValidationFlag.REQUIRED,
  maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
};

const DATA_POINT_ID_VALIDATION_OPTIONS = {
  allowedLeafTypes: [LeafType.TEXT, ...NUMERICAL_LEAF_TYPES],
  nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
  listType: ColumnTypeValidationFlag.NOT_ALLOWED,
  maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
};

const FEATURE_NAME_VALIDATION_OPTIONS = {
  allowedLeafTypes: [LeafType.TEXT],
  nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
  listType: ColumnTypeValidationFlag.NOT_ALLOWED,
  maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
};

const PREDICTED_OUTPUT_VALIDATION_OPTIONS = {
  allowedLeafTypes: [LeafType.DECIMAL],
  nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
  listType: ColumnTypeValidationFlag.NOT_ALLOWED,
  maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
};

const ACTUAL_OUTPUT_VALIDATION_OPTIONS = {
  allowedLeafTypes: [LeafType.DECIMAL],
  nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
  listType: ColumnTypeValidationFlag.NOT_ALLOWED,
  maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
};

export const FEATURE_DATA_DATA_FRAME_NAME = 'Feature data';
export const FEATURE_DESCRIPTIONS_DATA_FRAME_NAME = 'Feature descriptions';
export const LABEL_DATA_DATA_FRAME_NAME = 'Label data';
export const LABEL_DESCRIPTIONS_DATA_FRAME_NAME = 'Label description';

export const MODEL_TRAINING_INPUT_DATA_FRAMES_NAME = 'Model training input';

export const ALL_DATA_DATA_FRAME_NAME = 'All data';
export const ALL_DATA_COLUMNS_MAPPING: AllDataColumnsMapping = {
  dataPointId: 'data_point_id',
  predictedOutput: 'predicted_output',
  actualOutput: 'actual_output',
  testTrain: 'test_train',
  features: 'features',
  datetime: 'datetime',
  grouping: 'grouping',
  predictionCrossVal: 'prediction_cross_val',
};
export const ALL_DATA_VALIDATION_MAP: ColumnValidationMap<AllDataColumns> = {
  dataPointId: DATA_POINT_ID_VALIDATION_OPTIONS,
  predictedOutput: PREDICTED_OUTPUT_VALIDATION_OPTIONS,
  actualOutput: ACTUAL_OUTPUT_VALIDATION_OPTIONS,
  testTrain: {
    allowedLeafTypes: [LeafType.TEXT],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  features: {
    allowedLeafTypes: [LeafType.JSON],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  datetime: {
    allowedLeafTypes: [LeafType.DATETIME],
    nullableType: ColumnTypeValidationFlag.ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  grouping: {
    allowedLeafTypes: [LeafType.TEXT, LeafType.INTEGER],
    nullableType: ColumnTypeValidationFlag.ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  predictionCrossVal: PREDICTED_OUTPUT_VALIDATION_OPTIONS,
};

export const FEATURE_IMPORTANCE_DATA_FRAME_NAME = 'Feature importance';
export const FEATURE_IMPORTANCE_COLUMNS_MAPPING: FeatureImportanceColumnsMapping = {
  featureName: 'feature_name',
  importance: 'importance',
  baseFeatures: 'base_features',
  baseEntities: 'base_entities',
  description: 'description',
};

export const FEATURE_IMPORTANCE_VALIDATION_MAP: ColumnValidationMap<FeatureImportanceColumns> = {
  featureName: FEATURE_NAME_VALIDATION_OPTIONS,
  importance: {
    allowedLeafTypes: [LeafType.DECIMAL],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  baseFeatures: BASE_FEATURES_VALIDATION_OPTIONS,
  baseEntities: {
    allowedLeafTypes: [LeafType.TEXT],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.REQUIRED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  description: {
    allowedLeafTypes: [LeafType.TEXT],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
};

export const MODEL_EVALUATION_DATA_FRAME_NAME = 'Model evaluation';

export const SHAP_VALUES_DATA_FRAME_NAME = 'SHAP values';
export const SHAP_VALUES_COLUMNS_MAPPING: ShapValuesColumnsMapping = {
  featureName: 'feature_name',
  baseFeatures: 'base_features',
  baseEntities: 'base_entities',
  featureValues: 'feature_values',
  shapValue: 'shap_value',
  predictedOutput: 'predicted_output',
  actualOutput: 'actual_output',
  dataPointId: 'data_point_id',
};
export const SHAP_VALUES_VALIDATION_MAP: ColumnValidationMap<ShapValuesColumns> = {
  featureName: FEATURE_NAME_VALIDATION_OPTIONS,
  baseFeatures: BASE_FEATURES_VALIDATION_OPTIONS,
  baseEntities: BASE_ENTITIES_VALIDATION_OPTIONS,
  featureValues: {
    allowedLeafTypes: [LeafType.JSON],
    nullableType: ColumnTypeValidationFlag.ALLOWED,
    listType: ColumnTypeValidationFlag.ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.ALLOWED,
  },
  shapValue: {
    allowedLeafTypes: [LeafType.DECIMAL],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  predictedOutput: PREDICTED_OUTPUT_VALIDATION_OPTIONS,
  actualOutput: ACTUAL_OUTPUT_VALIDATION_OPTIONS,
  dataPointId: DATA_POINT_ID_VALIDATION_OPTIONS,
};

export const SEARCH_RANKINGS_DATA_FRAME_NAME = 'Search rankings';
export const FEATURE_CONTOUR_PLOT_FILES_NAME = 'contour_plots_partial_dependence';

export const PREDICTION_DEFAULTS_DATA_FRAME_NAME = 'Predictions default values';
export const PREDICTIONS_GRID_DATA_FRAME_NAME = 'Predictions grid';
export const PREDICTIONS_GRID_PREDICTED_VALUE_COLUMN_SYSTEM_NAME = 'predicted_value';
